import { useCallback, useMemo } from "react";
import useSWR from "swr";
import { mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { CustomMaterialGrade, CustomMaterialInput } from "./types";

const basePath = (isAdmin?: boolean) => `${isAdmin ? "/admin" : ""}/modules/lock_calculator`;

/** The signed-in user's library of custom materials (with how many calculations use each). */
export const useCustomMaterials = (signal: AbortSignal, isAdmin?: boolean) => {
  const api = useApi();
  const url = `${basePath(isAdmin)}/custom_materials`;
  return useSWR<{ items: CustomMaterialGrade[] }, CustomError, string>(url, (requestUrl) =>
    api.get<{ items: CustomMaterialGrade[] }>({ url: requestUrl, signal }),
  );
};

export const useCustomMaterialsApi = (isAdmin?: boolean) => {
  const api = useApi();
  const base = basePath(isAdmin);
  const listUrl = `${base}/custom_materials`;

  // Both the library and the calculator's material select list these grades.
  const refresh = useCallback(() => mutateCache([listUrl, `${base}/selects/material_grades`]), [base, listUrl]);

  const create = useCallback(
    async (body: CustomMaterialInput) => {
      const created = await api.post<CustomMaterialGrade, CustomMaterialInput>(listUrl, body);
      await refresh();
      return created;
    },
    [api, listUrl, refresh],
  );

  const update = useCallback(
    async (id: string, body: CustomMaterialInput) => {
      const updated = await api.putWithResult<CustomMaterialInput, CustomMaterialGrade>(`${listUrl}/${id}`, body);
      await refresh();
      return updated;
    },
    [api, listUrl, refresh],
  );

  // calculationId: the calculation being edited. The server detaches it from the material first, so it
  // does not block the deletion; any other calculation still using the material makes it answer 409.
  const remove = useCallback(
    async (id: string, calculationId?: string) => {
      await api.del(calculationId ? `${listUrl}/${id}?calculationId=${calculationId}` : `${listUrl}/${id}`);
      await refresh();
    },
    [api, listUrl, refresh],
  );

  return useMemo(() => ({ create, update, remove }), [create, update, remove]);
};
